import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete, TextField, FormControlLabel, Switch, Tooltip, InputAdornment } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const dataScopeOptions = [
  { label: 'My Program Only', value: 'my_program' },
  { label: 'All Programs in ED3N', value: 'all_programs' }
];

const infoIconStyle = {
  fontSize: '1.1rem',
  color: 'var(--info-color)',
  cursor: 'pointer',
  pointerEvents: 'auto',
  marginLeft: '4px',
  verticalAlign: 'middle',
};

const PlotOptionsDialog = ({ 
  open, 
  onClose, 
  onSave, 
  currentDataScope, 
  isLoading = false,
  showTestResultSelector = false,
  testResultOptions = [],
  currentTestResult = null,
  showCutoffLinesToggle = false,
  currentShowCutoffLines = false,
  hasCutoffData = true,
  cutoffDisabledMessage = 'No cutoff data available for this analyte',
  showRegressionLineToggle = false,
  currentShowRegressionLine = false,
  hasRegressionData = true,
  regressionDisabledMessage = 'No regression data available',
  showDensityCloudToggle = false,
  currentShowDensityCloud = false,
  hasDensityData = true,
  densityDisabledMessage = 'No population density data available'
}) => {
  const [dataScope, setDataScope] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showCutoffLines, setShowCutoffLines] = useState(false);
  const [showRegressionLine, setShowRegressionLine] = useState(false);
  const [showDensityCloud, setShowDensityCloud] = useState(false);

  const isTestResultDisabled = !testResultOptions || testResultOptions.length === 0;
  const testResultDisabledMessage = 'No patient test results available';

  // Reset dataScope and testResult when dialog opens or current values change
  React.useEffect(() => {
    setDataScope(currentDataScope ? dataScopeOptions.find(opt => opt.value === currentDataScope) : null);
    setTestResult(currentTestResult);
    setShowCutoffLines(currentShowCutoffLines);
    setShowRegressionLine(currentShowRegressionLine);
    setShowDensityCloud(currentShowDensityCloud);
  }, [currentDataScope, currentTestResult, currentShowCutoffLines, currentShowRegressionLine, currentShowDensityCloud, open]);

  const handleSave = async () => {
    await onSave({
      dataScope: dataScope?.value,
      testResult: testResult,
      showCutoffLines: showCutoffLines,
      showRegressionLine: showRegressionLine,
      showDensityCloud: showDensityCloud
    });
    onClose();
  };

  const handleCancel = () => {
    // Reset to current values
    setDataScope(currentDataScope ? dataScopeOptions.find(opt => opt.value === currentDataScope) : null);
    setTestResult(currentTestResult);
    setShowCutoffLines(currentShowCutoffLines);
    setShowRegressionLine(currentShowRegressionLine);
    setShowDensityCloud(currentShowDensityCloud);
    onClose();
  };

  const renderDisabledInfoIcon = (message) => (
    <Tooltip title={message} placement="top" arrow>
      <InfoOutlinedIcon
        style={infoIconStyle}
        tabIndex={0}
        role="button"
        aria-label={message}
      />
    </Tooltip>
  );

  // render an icon inside the disabled input, at the end before the dropdown arrow
  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Plot Options</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={dataScopeOptions}
          value={dataScope}
          onChange={(event, newValue) => setDataScope(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Data Scope"
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
        />
        {showTestResultSelector && (
          <Autocomplete
            options={testResultOptions}
            value={testResult}
            onChange={(event, newValue) => setTestResult(newValue)}
            getOptionLabel={(option) => option?.label || ''}
            renderOption={(props, option) => <li {...props} key={option.testId}>{option.label}</li>}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Show Patient Test Result"
                variant="outlined"
                margin="normal"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {/* render an icon inside the disabled input, at the end before the dropdown arrow */}
                      {isTestResultDisabled && (
                        <InputAdornment position="end" sx={{ pointerEvents: 'auto' }}>
                          {renderDisabledInfoIcon(testResultDisabledMessage)}
                        </InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option?.testId === value?.testId}
            disabled={isTestResultDisabled}
          />
        )}
        {showCutoffLinesToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={showCutoffLines}
                onChange={(event) => setShowCutoffLines(event.target.checked)}
                color="primary"
                disabled={!hasCutoffData}
              />
            }
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                Display Cutoff Lines
                {!hasCutoffData && renderDisabledInfoIcon(cutoffDisabledMessage)}
              </span>
            }
            style={{ marginTop: '16px' }}
            disabled={!hasCutoffData}
          />
        )}
        {showRegressionLineToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={showRegressionLine}
                onChange={(event) => setShowRegressionLine(event.target.checked)}
                color="primary"
                disabled={!hasRegressionData}
              />
            }
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                Display Regression Line
                {!hasRegressionData && renderDisabledInfoIcon(regressionDisabledMessage)}
              </span>
            }
            style={{ marginTop: '16px' }}
            disabled={!hasRegressionData}
          />
        )}
        {showDensityCloudToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={showDensityCloud}
                onChange={(event) => setShowDensityCloud(event.target.checked)}
                color="primary"
                disabled={!hasDensityData}
              />
            }
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                Display Density Cloud
                {!hasDensityData && renderDisabledInfoIcon(densityDisabledMessage)}
              </span>
            }
            style={{ marginTop: '16px' }}
            disabled={!hasDensityData}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={!dataScope || isLoading}>
          {isLoading ? 'Loading...' : 'Save'}
        </Button>
        <Button onClick={handleCancel} variant="outlined" disabled={isLoading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlotOptionsDialog;
